import { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Button,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import TextInput from "../../../../../../components/textinput/textinput";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import { currencyFormatter } from "../../../../../../utils/Formatter";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  deleteInsurancePolicy,
  updateInsuranceDetailsRequest,
} from "../../../../../../utils/APICalls";
import { getInsurancesRequest } from "../../../../../../redux/inspection-firms/slice";
import Loading from "../../../../../../components/loading/loading";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
  addPolicyCase?: boolean;
};

interface FormValues {
  insurerName: string;
  policyAmount: string;
  expiryDate: string;
  policyCopy: File | null;
}

export default function EditInsurance({
  showDialog,
  closeDialog,
  data,
  addPolicyCase = false,
}: props) {
  const dispatch = useDispatch();
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<any>();
  const [uploadedPolicy, setUploadedPolicy] = useState<any>(
    data?.policyDocumentName
  );

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    setSelectedFile(file);

    if (!file) {
      formik.setFieldValue("policyCopy", null);
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      formik.setFieldError(
        "policyCopy",
        "Unsupported file type. Only PDF, DOC, DOCX, JPG, or PNG files are allowed."
      );
      formik.setFieldValue("policyCopy", file);
      return;
    }

    if (!isFileSizeValid(file)) {
      formik.setFieldValue("policyCopy", file);
      return;
    }

    formik.setFieldValue("policyCopy", file);
    formik.setFieldError("policyCopy", undefined);
  };

  const isFileSizeValid = (file: any) => {
    if (file?.size === 0) {
      formik.setFieldError("policyCopy", "Empty files are not allowed.");
      return false;
    } else if (file?.size > 5000000) {
      formik.setFieldError("policyCopy", "File size exceeds 5MB limit.");
      return false;
    }
    return true;
  };

  const handleFileDelete = () => {
    setSelectedFile(null);
    formik.setFieldValue("policyCopy", null);
    formik.setFieldError("policyCopy", undefined);
  };

  const ValidationSchema = yup.object({
    insurerName: yup.string().required("This field can't be empty"),
    policyAmount: yup
      .string()
      .required("This field can't be empty")
      .test("min-value", "Policy amount must be greater than 0", (value) => {
        if (!value) return true;
        const numericValue = parseFloat(value.replace(/[^0-9.]/g, ""));
        return numericValue > 0;
      }),
    expiryDate: yup
      .date()
      .required("This field can't be empty")
      .min(dayjs().add(0, "day"), "Expiry date must be in the future"),
    policyCopy: yup
      .mixed()
      .nullable()
      .test("fileEmpty", "Empty files are not allowed", (value) => {
        if (!value) return true;
        if (value instanceof File) {
          return value.size > 0;
        }
        return true;
      })
      .test("fileSize", "File size exceeds 5MB limit.", (value) => {
        if (!value) return true;
        if (value instanceof File) {
          return value.size <= 5000000;
        }
        return true;
      })
      .test(
        "fileType",
        "Unsupported file type. Only PDF, DOC, DOCX, JPG, or PNG files are allowed.",
        (value) => {
          if (!value) return true;
          const allowedTypes = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "image/jpeg",
            "image/jpg",
            "image/png",
          ];
          if (value instanceof File) {
            return allowedTypes.includes(value.type);
          }
          return true;
        }
      ),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      insurerName: data?.insurerName || "",
      policyAmount: data?.policyAmount || "",
      expiryDate: data?.expiryDate || null,
      policyCopy: data?.policyCopy || null,
    },
    validationSchema: ValidationSchema,
    onSubmit: async (values) => {
      const isValid = await formik.validateForm();
      if (!isValid) {
        formik.setTouched({
          insurerName: true,
          policyAmount: true,
          expiryDate: true,
          policyCopy: true,
        });
        return;
      }

      updatePolicyDetails(values);
    },
    enableReinitialize: true,
  });

  const updatePolicyDetails = async (values: any) => {
    const formData = new FormData();
    formData.append("type", data?.type || "");
    formData.append("insurerName", values.insurerName || "");
    formData.append("policyAmount", values.policyAmount || "");
    formData.append("expiryDate", values.expiryDate || "");
    if (values.policyCopy) {
      formData.append("policyCopy", values.policyCopy);
    }
    if (data?.policyDocumentName) {
      formData.append("deletePolicyFile", uploadedPolicy ? "false" : "true");
    }
    setShowLoading(true);
    const response = await updateInsuranceDetailsRequest(data?.id, formData);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getInsurancesRequest({ id: data?.id }));
      hideDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const hideDialog = () => {
    formik.resetForm();
    closeDialog();
  };

  const handleRemovePolicy = async () => {
    setShowLoading(true);
    const response = await deleteInsurancePolicy(data?.id, data?.type);
    setShowLoading(false);
    if (response?.status === 200 || response?.status === 204) {
      dispatch(getInsurancesRequest({ id: data?.id }));
      hideDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <DialogContainer
      title="Inspection Firm - Insurance"
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={formik.handleSubmit}
      leftButtonComponent={
        !addPolicyCase &&
        data && (
          <Button
            sx={{ mr: 2 }}
            startIcon={<DeleteOutlinedIcon color="error" />}
            color="error"
            onClick={handleRemovePolicy}
          >
            Remove Policy
          </Button>
        )
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className="user-name-layout">
          <Typography className="label-txt">{data?.label}*</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <TextInput
              sx={{ width: "100% !important" }}
              className="field-input"
              id="insurerName"
              name="insurerName"
              variant="outlined"
              placeholder="Insurer"
              label="Insurer"
              value={formik.values.insurerName}
              onChange={(e) => {
                formik.setFieldValue("insurerName", e.target.value);
              }}
              error={
                formik.touched.insurerName && Boolean(formik.errors.insurerName)
              }
              helperText={
                formik.touched.insurerName && formik.errors.insurerName
              }
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "start",
                mt: 2,
              }}
            >
              <TextInput
                sx={{ flex: 1 }}
                className="field-input"
                id="policyAmount"
                name="policyAmount"
                variant="outlined"
                label="Policy Amount*"
                placeholder="Policy Amount"
                value={formik.values.policyAmount || ""}
                onChange={(e) => {
                  const formatted = currencyFormatter(e.target.value);
                  formik.setFieldValue("policyAmount", formatted);
                }}
                error={
                  formik.touched.policyAmount &&
                  Boolean(formik.errors.policyAmount)
                }
                helperText={
                  formik.touched.policyAmount && formik.errors.policyAmount
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
              <Box sx={{ flex: 1, ml: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Expiration Date*"
                    sx={{
                      width: "100% !important",
                      height: 56,
                      "& .MuiInputBase-root": {
                        "& .MuiInputBase-input": {
                          padding: 2,
                        },
                      },
                      pb: 1,
                    }}
                    value={
                      formik.values.expiryDate
                        ? dayjs(formik.values.expiryDate)
                        : null
                    }
                    onChange={(date) => {
                      formik.setFieldValue(
                        "expiryDate",
                        date?.format("YYYY-MM-DD")
                      );
                    }}
                    minDate={dayjs().add(1, "day")}
                    format="MMMM D, YYYY"
                    slotProps={{
                      inputAdornment: {
                        position: "start",
                      },
                      textField: {
                        error:
                          formik.touched.expiryDate &&
                          Boolean(formik.errors.expiryDate),
                        helperText:
                          formik.touched.expiryDate && formik.errors.expiryDate,
                        inputProps: {
                          readOnly: true,
                          style: { cursor: "pointer" },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
            {!uploadedPolicy && !formik.values.policyCopy && (
              <Box
                sx={{
                  height: 80,
                  width: "100%",
                  border: "1px dashed rgba(0, 0, 0, 0.12)",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <UploadFileIcon sx={{ ml: 5 }} color="primary" />
                <Box ml={2}>
                  <Box sx={{ display: "flex" }}>
                    <Typography
                      color="primary"
                      sx={{ textDecoration: "underline" }}
                    >
                      Click to upload
                    </Typography>
                    <Typography sx={{ ml: 1 }}>or drag and drop</Typography>
                  </Box>
                  <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                    PDF, DOC, DOCX, JPG or PNG (max. 5MB)
                  </Typography>
                </Box>
                <input
                  style={{
                    display: "block",
                    height: 80,
                    width: "100%",
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    opacity: 0,
                    cursor: "pointer",
                  }}
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/jpg,image/png"
                  onChange={handleFileChange}
                  name="policyCopy"
                  onClick={(event) => {
                    // same file selection issue
                    (event.target as HTMLInputElement).value = "";
                  }}
                />
              </Box>
            )}
            {(formik.values.policyCopy || selectedFile) && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: formik.errors.policyCopy
                    ? "1.5px solid red"
                    : "1.5px solid green",
                  p: 0.8,
                  borderRadius: 1,
                  backgroundColor: formik.errors.policyCopy
                    ? "#FFF5F5"
                    : "#F7FAF7",
                }}
              >
                <Box display={"flex"} alignItems={"center"}>
                  <AttachFileOutlinedIcon color="primary" />
                  <Box sx={{ ml: 1.5 }}>
                    <Typography>
                      {selectedFile?.name ||
                        formik.values.policyCopy?.name ||
                        "No file selected"}
                    </Typography>
                    <Typography>
                      {" "}
                      {selectedFile
                        ? `${(selectedFile.size / 1024).toFixed(2)} kb`
                        : formik.values.policyCopy
                        ? `${(formik.values.policyCopy.size / 1024).toFixed(
                            2
                          )} kb`
                        : ""}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <IconButton aria-label="delete" onClick={handleFileDelete}>
                    <RemoveCircleOutlineIcon color="error" />
                  </IconButton>
                </Box>
              </Box>
            )}
            {uploadedPolicy && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: "1.5px solid green",
                  p: 0.8,
                  borderRadius: 1,
                  backgroundColor: "#F7FAF7",
                }}
              >
                <Box display={"flex"} alignItems={"center"}>
                  <AttachFileOutlinedIcon color="primary" />
                  <Box sx={{ ml: 1.5 }}>
                    <Typography>{uploadedPolicy}</Typography>
                    <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                      Complete
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleOutlineIcon color="success" sx={{ mr: 2 }} />
                  <IconButton
                    aria-label="delete"
                    onClick={() => {
                      setUploadedPolicy("");
                    }}
                  >
                    <DeleteOutlinedIcon color="error" />
                  </IconButton>
                </Box>
              </Box>
            )}
            {formik.errors.policyCopy && (
              <Typography sx={{ fontSize: 12, mt: 0.5 }} color="error">
                {formik.errors.policyCopy}
              </Typography>
            )}
          </Box>
        </Box>
      </form>
      {showLoading && <Loading />}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </DialogContainer>
  );
}
